import { EmptyMessage } from '@/components/atoms/EmptyMessage';
import { useMemo } from 'react';
import { sendAnalytics } from '@/services/analyticsService';
import { useUsersData } from '../../hooks/useUsersData';
import { useReportData } from '../../hooks/useReportData';
import { Combobox, ItemsListProps } from '../atoms/Combobox';
import { useUserData } from '../../hooks/useUserData';

type EmailCampaignsProps = {
	selectedEmail?: string;
	onUpdateUserEmail: (email: string) => void;
};

export const EmailCampaigns = ({
	selectedEmail,
	onUpdateUserEmail
}: EmailCampaignsProps) => {
	const { report } = useReportData();

	const { user } = useUserData();

	const { users } = useUsersData();

	const usersList = useMemo(() => {
		if (users.length === 0) return [];

		const sortedUsers = users.sort(
			({ email: email1 }, { email: email2 }) => {
				const sortEmail1 = email1.toLowerCase();
				const sortEmail2 = email2.toLowerCase();

				if (sortEmail1 < sortEmail2) {
					return -1;
				}
				if (sortEmail1 > sortEmail2) {
					return 1;
				}

				return 0;
			}
		);

		const formattedList: ItemsListProps = sortedUsers.map(
			({ id, email }) => ({
				id: id || email,
				label: email,
				value: email
			})
		);

		return formattedList;
	}, [users]);

	const handleResetEmailSelection = () => {
		onUpdateUserEmail(user.email);
		sendAnalytics({
			type: 'track',
			eventName: 'User reset email selection'
		});
	};

	return (
		<>
			<section className="relative flex w-full flex-col items-end justify-center">
				<button
					type="button"
					onClick={handleResetEmailSelection}
					disabled={user.email === selectedEmail}
					data-is-visible={user.email !== selectedEmail}
					className="absolute right-0 top-0 translate-y-full text-xs text-orange opacity-0 transition-opacity data-[is-visible=true]:opacity-100 md:-translate-y-full"
				>
					reset
				</button>

				<div className="flex w-full flex-col gap-3 md:flex-row md:items-center">
					<p className="font-semibold text-orange">Account:</p>

					<Combobox
						itemsList={usersList}
						placeholder="User email"
						emptyMessage="No user found."
						selectedItem={selectedEmail}
						onUpdateSelectedItem={onUpdateUserEmail}
						hideClearSelection
					/>
				</div>
			</section>

			<hr className="w-full rounded-full border border-dark" />

			{report.emails?.total && report.emails?.total > 0 ? (
				<div className="flex w-full gap-6 text-lightest-grey">
					<section className="flex w-full flex-col gap-6">
						<span className="grid grid-cols-2 gap-2">
							<p className="font-semibold">
								{report.emails?.total}{' '}
							</p>
							<p className="text-lighter-grey">Sent</p>
						</span>

						<span className="grid grid-cols-2 gap-1">
							<p className="font-semibold">
								{Math.trunc(
									((report.emails?.opened ?? 0) * 100) /
										report.emails?.total
								)}{' '}
								%
							</p>
							<p className="text-lighter-grey">Open rate</p>
						</span>
					</section>

					<hr className="h-full w-1 rounded-full border-transparent bg-dark" />

					<span className="grid w-full grid-cols-2 gap-2">
						<p className="font-semibold">
							{report.emails?.opened}{' '}
						</p>
						<p className="text-lighter-grey">Opened</p>
					</span>
				</div>
			) : (
				<EmptyMessage
					title="No activity to report yet!"
					message="Begin making Lists to send out emails and start generating more leads."
					className="relative h-full"
				/>
			)}
		</>
	);
};
